import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Drama } from "@/lib/queries";

type HeroSectionProps = {
  featured: Drama[];
  hot: Drama[];
};

export default function HeroSection({ featured, hot }: HeroSectionProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (featured.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [featured.length]);

  if (featured.length === 0) return null;

  const current = featured[index];

  const go = (next: number) => {
    setIndex((next + featured.length) % featured.length);
  };

  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            <div
              className="group relative h-[380px] w-full overflow-hidden rounded-lg md:h-[460px] lg:h-[520px]"
              onTouchStart={(event) => {
                touchStartX.current = event.touches[0].clientX;
              }}
              onTouchEnd={(event) => {
                if (touchStartX.current === null) return;
                const delta = event.changedTouches[0].clientX - touchStartX.current;
                if (Math.abs(delta) > 50) {
                  go(delta < 0 ? index + 1 : index - 1);
                }
                touchStartX.current = null;
              }}
            >
              {featured.map((drama, i) => (
                <div
                  key={drama.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    i === index ? "opacity-100" : "pointer-events-none opacity-0"
                  }`}
                >
                  <img
                    src={drama.cover}
                    alt={`${drama.title} 焦点剧集封面`}
                    title={`${drama.title} 剧情介绍与演员资料`}
                    className="h-full w-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-transparent" />
                </div>
              ))}

              <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-label text-foreground-950">
                  <i className="ri-star-smile-line" />
                  {current.status === "连载" ? "热播推荐" : "精选重温"}
                </span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
                  {current.title}
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-background-100/85 md:text-base">
                  {current.summary}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/detail/${current.id}`)}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-play-circle-line text-base" />
                    查看剧情详情
                  </button>
                  <div className="flex items-center gap-3 text-xs font-label text-background-100/80">
                    <span>{current.year}</span>
                    <span className="h-3 w-px bg-background-100/30" />
                    <span>{current.category}</span>
                    <span className="h-3 w-px bg-background-100/30" />
                    <span>全 {current.episodes} 集</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                aria-label="上一张"
                onClick={() => go(index - 1)}
                className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-base text-background-50 transition-colors hover:bg-foreground-950/70 md:left-5 md:h-10 md:w-10"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => go(index + 1)}
                className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-base text-background-50 transition-colors hover:bg-foreground-950/70 md:right-5 md:h-10 md:w-10"
              >
                <i className="ri-arrow-right-s-line" />
              </button>

              <div className="absolute bottom-5 right-5 hidden gap-1.5 md:flex">
                {featured.map((drama, i) => (
                  <button
                    key={drama.id}
                    type="button"
                    aria-label={`切换到 ${drama.title}`}
                    onClick={() => go(i)}
                    className={`h-1.5 cursor-pointer rounded-full transition-all ${
                      i === index ? "w-6 bg-accent-400" : "w-1.5 bg-background-50/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="border-b border-background-200 pb-4">
                <span className="text-xs font-label tracking-[0.25em] text-accent-700">
                  本期精选
                </span>
                <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950">
                  {current.title}
                </h3>
                <p className="mt-2 line-clamp-4 text-[13px] leading-6 text-foreground-600">
                  {current.synopsis}
                </p>
                <Link
                  to={`/detail/${current.id}`}
                  className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-label text-primary-600 transition-colors hover:text-primary-700"
                >
                  阅读完整剧情
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <h4 className="font-heading text-base font-semibold text-foreground-950">
                  <Link to="/ranking" className="transition-colors hover:text-primary-600">
                    热门剧集榜
                  </Link>
                </h4>
                <Link
                  to="/ranking"
                  className="text-xs font-label text-foreground-500 transition-colors hover:text-primary-600"
                >
                  完整榜单
                </Link>
              </div>

              <ul className="mt-3 flex flex-1 flex-col gap-1">
                {hot.map((drama, i) => (
                  <li key={drama.id}>
                    <Link
                      to={`/detail/${drama.id}`}
                      className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-label text-xs ${
                          i < 3
                            ? "bg-primary-600 text-background-50"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-foreground-900">
                          {drama.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] font-label text-foreground-400">
                          {drama.year} · {drama.status}
                        </span>
                      </span>
                      <span className="flex items-center gap-1 text-[11px] font-label text-accent-700">
                        <i className="ri-fire-line" />
                        {drama.heat}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}